import { FC, useMemo, useState } from 'react';
import clsx from 'clsx';
import { getNonStickyTop, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { toRem } from '@marriott/mi-ui-library';
import {
  BASELINE_SEARCH_FORM_HEIGHTS,
  NGSF_CONTAINER_GUTTERS,
  NGSF_CONTAINER_MAX_WIDTHS,
  SkeletonLoaderBreakpoint,
  StyledSkeletonLoaderDiv,
  StyledSkeletonLoaderFormContainer,
} from './SearchFormSkeletonLoader.styles';

interface SearchFormSkeletonLoaderProps {
  enableOverlayTop: boolean;
}

type BreakpointOffsets = Record<SkeletonLoaderBreakpoint, number>;

const BREAKPOINTS: SkeletonLoaderBreakpoint[] = ['xs', 'sm', 'md', 'lg'];

/**
 * For en-US we have a global-nav component that sits above the SearchForm. For other locales we don't have a global-nav component, so the SearchForm sits at the top of the page. We need to detect whether the global-nav is present in order to compute the correct top offset for the skeleton loader.
 * As the scope for NGSF A/B testing is limited to en-US, we can safely assume that if the global-nav is present, it will be in the model for en-US pages. For other locales, we can assume that the global-nav is not present.
 * /
// const GLOBAL_NAV_RESOURCE_TYPE = 'mi-aem-homepage-spa/components/content/global-navigation';

// /**
//  * Recursively walks the page model and detects the global-nav component by
//  * resource type, regardless of where it sits in the model tree.
//  */
// const detectHasGlobalNavFromModel = (model: Record<string, unknown> | undefined): boolean => {
//   const hasGlobalNavInNode = (node: unknown): boolean => {
//     if (!node) return false;

//     if (Array.isArray(node)) {
//       return node.some(hasGlobalNavInNode);
//     }

//     if (typeof node !== 'object') return false;

//     const obj = node as Record<string, unknown>;
//     if (obj['type'] === GLOBAL_NAV_RESOURCE_TYPE || obj[':type'] === GLOBAL_NAV_RESOURCE_TYPE) {
//       return true;
//     }

//     return Object.values(obj).some(hasGlobalNavInNode);
//   };

//   return hasGlobalNavInNode(model);
// };

// /**
//  * Resolve hasGlobalNav from model sources only (no DOM selector fallback) to
//  * avoid post-hydration flips that cause skeleton top jumps.
//  */
// const detectHasGlobalNavStable = (pageModel: Record<string, unknown> | undefined): boolean => {
//   if (detectHasGlobalNavFromModel(pageModel)) return true;

//   if (typeof window !== 'undefined') {
//     const windowModel = (window as unknown as { pageProps?: { model?: Record<string, unknown> } })?.pageProps?.model as
//       | Record<string, unknown>
//       | undefined;
//     return detectHasGlobalNavFromModel(windowModel);
//   }

//   return false;
// };

/**
 * Detect Polaris/NorthStar variant. Reads `model.isNorthStarPage` (set in
 * `apps/mi-homepage-renderer/pages/[[...page]].tsx`); falls back to
 * `window.pageProps.model.isNorthStarPage` and the `polaris-page` body class
 * applied in `apps/mi-homepage-renderer/pages/_document.tsx`.
 */
const detectIsNorthStar = (pageModel: Record<string, unknown> | undefined): boolean => {
  if (pageModel?.['isNorthStarPage']) return true;
  if (typeof window !== 'undefined') {
    const windowModel = (window as unknown as { pageProps?: { model?: Record<string, unknown> } })?.pageProps?.model as
      | Record<string, unknown>
      | undefined;
    if (windowModel?.['isNorthStarPage']) return true;
    return !!document.body?.classList?.contains('polaris-page');
  }
  return false;
};

const computeBreakpointOffsets = (isNorthStar: boolean): BreakpointOffsets =>
  BREAKPOINTS.reduce<BreakpointOffsets>(
    (acc, bp) => {
      acc[bp] = getNonStickyTop(bp, isNorthStar, true);
      return acc;
    },
    { xs: 0, sm: 0, md: 0, lg: 0 }
  );

/** Convert an NGSF container max-width entry to a CSS length. */
const ngsfMaxWidthToCss = (bp: SkeletonLoaderBreakpoint): string => {
  const value = NGSF_CONTAINER_MAX_WIDTHS[bp];
  return value === 'none' ? '100%' : toRem(value);
};

/**
 * Skeleton loader rendered while the AB test target call is in flight.
 *
 * Position parity with `StickyComponentWrapper`:
 * - Uses the shared `getNonStickyTop(breakpoint, isNorthStar, hasGlobalNav)`
 *   so the skeleton sits at the same offset the real SearchForm will land at.
 * - When `enableOverlayTop` is true the skeleton is absolutely positioned over
 *   the herobanner, using stable offsets for its full lifecycle to avoid
 *   post-mount jump behavior.
 *
 * Dimensions:
 * - Height per breakpoint mirrors the baseline SearchForm pill
 *   (`BASELINE_SEARCH_FORM_HEIGHTS`).
 * - Width per breakpoint mirrors the Next-Gen SearchForm container
 *   (`NGSF_CONTAINER_MAX_WIDTHS`).
 */
export const SearchFormSkeletonLoader: FC<SearchFormSkeletonLoaderProps> = ({ enableOverlayTop }) => {
  const pageModel = useDCAPageModelStore(state => state.pageModel) as Record<string, unknown> | undefined;
  const [isNorthStar] = useState<boolean>(() => detectIsNorthStar(pageModel));
  // const [hasGlobalNav] = useState<boolean>(() => detectHasGlobalNavStable(pageModel));

  // StickyComponentWrapper parity: use shared non-sticky top offsets and keep
  // them stable for the skeleton lifecycle to avoid post-mount position jumps.
  const topOffsets: BreakpointOffsets = useMemo(() => computeBreakpointOffsets(isNorthStar), [isNorthStar]);

  // In overlay mode we drive the top from `getNonStickyTop` at each breakpoint;
  // otherwise the skeleton flows inline and no offset is needed.
  const topOffsetForBreakpoint = (bp: SkeletonLoaderBreakpoint): string =>
    enableOverlayTop ? toRem(topOffsets[bp]) : toRem(0);

  return (
    <StyledSkeletonLoaderFormContainer
      className={clsx({ 'search-form-position': enableOverlayTop })}
      $topOffsetXs={topOffsetForBreakpoint('xs')}
      $topOffsetSm={topOffsetForBreakpoint('sm')}
      $topOffsetMd={topOffsetForBreakpoint('md')}
      $topOffsetLg={topOffsetForBreakpoint('lg')}
      $heightXs={toRem(BASELINE_SEARCH_FORM_HEIGHTS.xs)}
      $heightSm={toRem(BASELINE_SEARCH_FORM_HEIGHTS.sm)}
      $heightMd={toRem(BASELINE_SEARCH_FORM_HEIGHTS.md)}
      $heightLg={toRem(BASELINE_SEARCH_FORM_HEIGHTS.lg)}
      $maxWidthXs={ngsfMaxWidthToCss('xs')}
      $maxWidthSm={ngsfMaxWidthToCss('sm')}
      $maxWidthMd={ngsfMaxWidthToCss('md')}
      $maxWidthLg={ngsfMaxWidthToCss('lg')}
      $gutterXs={toRem(NGSF_CONTAINER_GUTTERS.xs)}
      $gutterSm={toRem(NGSF_CONTAINER_GUTTERS.sm)}
      $gutterMd={toRem(NGSF_CONTAINER_GUTTERS.md)}
      $gutterLg={toRem(NGSF_CONTAINER_GUTTERS.lg)}
    >
      <StyledSkeletonLoaderDiv className="search-form-wrapper">
        <div className="search-form-skeleton">
          <img
            src="https://cache.marriott.com/content/dam/marriott-digital/cy/us-canada/hws/h/hpnnb/en_us/logo/internal/assets/cy-hpnnb-grey-image-placeholder-34740.png?imbypass=on"
            alt="loading"
            className="skeleton-image"
          />
        </div>
      </StyledSkeletonLoaderDiv>
    </StyledSkeletonLoaderFormContainer>
  );
};
